import React from 'react';
import styled from 'styled-components/macro';
import { useHistory } from 'react-router';
import first from 'lodash.first';
import delve from 'dlv';

import DropdownList, {
  DropdownListWrapper,
} from 'components/styled/DropdownList/DropdownList';
import CloseDropdownHandler from 'components/utility/CloseDropdownHandler/CloseDropdownHandler';

import theme from 'styles/themes/variables';
import { CompareModes, EntityTypes } from 'context/entityContext';

import { DropdownButton } from 'components/Header/TopBar/TopBar.style';

import { ReactComponent as IconCompare } from 'icons/icon-compare.svg';
import { AllTopicTypes } from 'topics';
import pluralize from 'helpers/pluralize';
import SubDropdownList, { Button } from './SubDropdownList';
import { ILabelValuePair } from 'types/shared';
import Anchor from 'components/base/Anchor/Anchor';
import getEntityUrl from 'url-constructors/getEntityUrl';

const CloseButton = styled('button')`
  text-decoration: none;
  position: relative;
  padding-bottom: 2px;
  box-shadow: rgb(11 214 167) 0px 2px 0px 0px;
  transition: color 150ms cubic-bezier(0.5, 0, 0.5, 1) 0s;
  cursor: pointer;
  margin: 0 auto;

  &:hover {
    box-shadow: rgb(11 214 167) 0px 3px 0px 0px;
  }
`;

const Paragraph = styled.p`
  text-align: center;
  padding: 0 1em;
`;

const DropdownContainer = styled('div')`
  @media screen and (min-width: 27.5em) {
    position: relative;
  }

  ${Paragraph} > ${CloseButton} {
    width: fit-content;
    padding: 0;

    &:hover {
      background: transparent;
    }
  }
`;

export const DropdownListContent = styled(DropdownList)`
  max-height: fit-content;
  position: initial;
`;

export const StyledDropdownListWrapper = CloseDropdownHandler(styled<any>(
  DropdownListWrapper
)`
  min-width: 14.5em;
  top: ${({ isStatic }): number => (isStatic ? 77 : 100)}%;
  padding: ${({ withoutPaddingBottom }): string =>
    `${theme.spacing.small} ${withoutPaddingBottom ? '0 0' : ''} 0;`}
  right: ${({ isStatic }): number => (isStatic ? 32 : 0)}px;
  max-width: 300px;
`);

interface ICompareDropdownProps {
  /** Is the dropdown currently visible? */
  isOpen: boolean;
  /** Handler that changes the isOpen prop */
  toggleDropdown: Function;
  /** The Entity IDs */
  entityIds?: string[];
  /** The Entity Type e.g. track, release, label */
  entityType?: EntityTypes | string;
  /** The current topic */
  topic?: AllTopicTypes[];
  /** The current compare mode (optional)  */
  compareMode?: CompareModes;
  /** The selected countryFilter */
  countryFilter?: string;
  /** Object containing all information for the entities in the Redux cache */
  entitiesById: {
    loading: boolean;
    error: boolean;
    entity: any;
    metadata: any;
  };
}

export type ItemType = ILabelValuePair<string> & { entityType: string };

const CompareDropdown: React.FC<ICompareDropdownProps> = ({
  isOpen,
  toggleDropdown,
  entityType,
  entityIds = [],
  topic,
  compareMode,
  countryFilter,
  entitiesById,
}) => {
  const topicString = topic ? topic.join('/') : '';
  const items = ['track', 'release', 'artist', 'project'].map(
    (entity: string) => ({
      value: entity,
      label: entity,
      entityType: entity,
    })
  );

  const entityItems = items.map(
    ({
      label,
      value,
      entityType,
    }: ILabelValuePair<string> & { entityType: string }) => ({
      entityType,
      label: pluralize(label),
      value: pluralize(value),
    })
  );
  const history = useHistory();
  const entityId = entityIds && entityIds.length > 0 ? entityIds[0] : '';

  return (
    <DropdownContainer data-dropdown-item data-pw="compare-dropdown">
      <DropdownButton
        onClick={toggleDropdown}
        aria-haspopup="true"
        aria-expanded={isOpen}
        isOpen={isOpen}
      >
        <IconCompare />
      </DropdownButton>
      {isOpen && (
        <StyledDropdownListWrapper overflowLeft toggleDropdown={toggleDropdown}>
          <DropdownListContent>
            {compareMode ? (
              <DropdownList>
                <li>
                  <Paragraph>
                    You need to close the current comparison to start a new one.
                  </Paragraph>

                  <Paragraph>
                    {entityId ? (
                      <Anchor
                        className="defaultAnchor"
                        to={getEntityUrl({
                          entityId: first(entityIds),
                          entityType,
                          topic: topic,
                          parentEntityId: delve(
                            entitiesById,
                            `${[first(entityIds)]}.entity.parent_group_id`
                          ),
                        })}
                        preserveSearch={true}
                      >
                        Close compare
                      </Anchor>
                    ) : (
                      <CloseButton onClick={(): void => history.goBack()}>
                        Close compare
                      </CloseButton>
                    )}
                  </Paragraph>
                </li>
              </DropdownList>
            ) : entityId ? (
              <>
                <li>
                  <Button
                    to={`/compare/entity/${entityType}/${entityIds[0]}/${topicString}`}
                    preserveSearch={true}
                  >
                    Compare {pluralize(entityType as string)}
                  </Button>
                </li>
                <li>
                  <Button
                    to={`/compare/country/${
                      countryFilter && !countryFilter.includes('EX')
                        ? countryFilter
                        : 'all'
                    }/entity/${entityType}/${entityIds[0]}/${topicString}`}
                    preserveSearch={true}
                  >
                    Compare countries
                  </Button>
                </li>
              </>
            ) : (
              <>
                <SubDropdownList
                  isCountry={false}
                  items={entityItems}
                  topicString={topicString}
                />
                <SubDropdownList
                  isCountry
                  items={items}
                  topicString={topicString}
                />
              </>
            )}
          </DropdownListContent>
        </StyledDropdownListWrapper>
      )}
    </DropdownContainer>
  );
};

export default CompareDropdown;
